import { MaybeLink } from '@app/components/various/MaybeLink';
import { Tooltip } from '@app/components/various/Tooltip';
import { composeEditorPreviewPath } from '@app/utils/composeEditorPreviewPath';
import { useTranslationsConfig } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Cms, Translations } from '@typings';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

interface Props {
  code: Translations.SupportedLanguagesCodes;
  status?: Cms.PageStatus;
  pageId?: string;
  size?: number;
  showTooltip?: boolean;
}

export const LanguageFlag = ({ code, status, pageId, size, showTooltip = true }: Props) => {
  const { t } = useTranslation(['cms']);
  const { supportedLanguages } = useTranslationsConfig();

  const statusLabelMap = {
    published: t('cms:published'),
    published_with_changes: t('cms:unpublished_changes'),
    unpublished: t('cms:unpublished'),
    unpublished_changes: t('cms:unpublished_changes'),
  } as const;

  const languageDetails = supportedLanguages.find(details => details.code === code);
  if (!isDefined(languageDetails)) {
    return null;
  }

  const FlagIcon = languageDetails.flagIcon;

  return (
    <MaybeLink
      title={t('cms:page_preview_with_language', { language: languageDetails.label, status: isDefined(status) && statusLabelMap[status] })}
      to={isDefined(pageId) ? composeEditorPreviewPath({ lang: code, pageId, pathType: 'preview' }) : undefined}
      className="block"
    >
      <Tooltip
        content={
          isDefined(status) &&
          showTooltip && (
            <div className="flex flex-col text-center">
              <div className="body-4">{languageDetails.label}</div>
              <div className="body-5">{statusLabelMap[status]}</div>
            </div>
          )
        }
      >
        <div className="relative flex">
          <FlagIcon
            className={cx({
              'opacity-30 grayscale': status === 'unpublished',
              'status-cutout': status === 'published_with_changes',
            })}
            size={size}
          />
          {status === 'published_with_changes' && <span className="absolute -right-1 -top-1 size-2 rounded-full bg-warning-48" />}
        </div>
      </Tooltip>
    </MaybeLink>
  );
};
